/* ==========================================================================
   Animation Test Suite - Verify Windows Animation Bypass
   Asian American Forward Website
   ========================================================================== */

import { isGSAPLoaded } from './utils/gsap-animations.js';
import { isCanvasSupported } from './utils/canvas-animations.js';
import { areWindowsAnimationsDisabled } from './utils/js-animations.js';

export function runAnimationTests() {
  console.log('🧪 Running Animation System Tests...');
  
  const results = {
    gsap: false,
    canvas: false,
    jsEngine: true,
    windowsAnimationsDisabled: false
  };
  
  // Test GSAP availability
  try {
    results.gsap = isGSAPLoaded();
    console.log(`✅ GSAP Test: ${results.gsap ? 'AVAILABLE' : 'NOT AVAILABLE'}`);
  } catch (error) {
    console.log('❌ GSAP Test: ERROR', error);
  }
  
  // Test Canvas support
  try {
    results.canvas = isCanvasSupported();
    console.log(`✅ Canvas Test: ${results.canvas ? 'SUPPORTED' : 'NOT SUPPORTED'}`);
  } catch (error) {
    console.log('❌ Canvas Test: ERROR', error);
  }
  
  // Test Windows animation status
  try {
    results.windowsAnimationsDisabled = areWindowsAnimationsDisabled();
    console.log(`✅ Windows Animations: ${results.windowsAnimationsDisabled ? 'DISABLED (Using fallback)' : 'ENABLED (Normal CSS)'}`);
  } catch (error) {
    console.log('❌ Windows Animation Detection: ERROR', error);
  }
  
  // Overall status
  if (results.gsap) {
    console.log('🎉 PRIMARY SYSTEM: GSAP animations active - animations will work regardless of Windows settings!');
  } else if (results.jsEngine) {
    console.log('⚡ FALLBACK SYSTEM: JavaScript animations active - should work with Windows settings disabled');
  } else if (results.canvas) {
    console.log('🎨 EMERGENCY SYSTEM: Canvas animations available - ultimate fallback');
  } else {
    console.log('⚠️ WARNING: No animation systems available - animations may not work');
  }
  
  return results;
}

// Test individual animation functions
export function testSpecificAnimations() {
  console.log('🎯 Testing Specific Animation Functions...');
  
  // Test hero text animation
  const heroLines = document.querySelectorAll('.hero-line');
  if (heroLines.length > 0) {
    console.log(`✅ Hero Lines Found: ${heroLines.length} elements`);
  } else {
    console.log('⚠️ Hero Lines: Not found (may not be on current page)');
  }
  
  // Test cards
  const heroCards = document.querySelectorAll('.hero-card');
  if (heroCards.length > 0) {
    console.log(`✅ Hero Cards Found: ${heroCards.length} elements`);
  } else {
    console.log('⚠️ Hero Cards: Not found (may not be on current page)');
  }
  
  // Test donate button
  const donateButton = document.querySelector('.nav-link--primary');
  if (donateButton) {
    console.log('✅ Donate Button Found: Rainbow border animation should be active');
  } else {
    console.log('⚠️ Donate Button: Not found');
  }
  
  // Test text ticker
  const textTicker = document.querySelector('.text-ticker');
  if (textTicker) {
    console.log('✅ Text Ticker Found: Scrolling animation should be active');
  } else {
    console.log('⚠️ Text Ticker: Not found (may not be implemented yet)');
  }
}

// Manual test functions you can call from browser console
export function manualTestGSAP() {
  if (!isGSAPLoaded()) {
    console.log('❌ GSAP not loaded - make sure GSAP is installed: npm install gsap');
    return;
  }
  
  // Import GSAP for the test
  import('gsap').then(({ gsap }) => {
    // Create a test element
    const testDiv = document.createElement('div');
    testDiv.style.cssText = `
      position: fixed;
      top: 50%;
      left: 50%;
      width: 100px;
      height: 100px;
      background: red;
      z-index: 9999;
      transform: translate(-50%, -50%);
    `;
    testDiv.textContent = 'GSAP Test';
    document.body.appendChild(testDiv);
    
    // Animate it
    gsap.to(testDiv, {
      rotation: 360,
      scale: 1.5,
      duration: 2,
      yoyo: true,
      repeat: -1,
      ease: "power2.inOut"
    });
    
    console.log('🎬 GSAP Test Animation Started - You should see a red spinning square');
    console.log('📝 Run stopGSAPTest() to remove it');
    
    // Store reference for cleanup
    window.gsapTestElement = testDiv;
  }).catch(error => {
    console.error('❌ Error loading GSAP for test:', error);
  });
}

export function stopGSAPTest() {
  if (window.gsapTestElement) {
    document.body.removeChild(window.gsapTestElement);
    delete window.gsapTestElement;
    console.log('🛑 GSAP Test Animation Stopped');
  }
}

// Expose test functions globally for easy console access
window.testAnimations = runAnimationTests;
window.testSpecific = testSpecificAnimations;
window.testGSAP = manualTestGSAP;
window.stopGSAPTest = stopGSAPTest;

console.log('🧪 Animation Test Suite Loaded');
console.log('📝 Available commands:');
console.log('   testAnimations() - Run full test suite');
console.log('   testSpecific() - Test specific elements');
console.log('   testGSAP() - Manual GSAP test');
console.log('   stopGSAPTest() - Stop GSAP test'); 